import React from 'react';
import { ChevronRight, Check } from 'lucide-react';

export const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
    borderLight: '#2A3F54',
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
};

export type SettingsSection = 'account' | 'notifications' | 'map' | 'billing';

export interface ToggleSwitchProps {
    enabled: boolean;
    onToggle: () => void;
    disabled?: boolean;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    enabled,
    onToggle,
    disabled = false,
}) => (
    <button
        onClick={onToggle}
        disabled={disabled}
        style={{
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            background: enabled ? colors.accent : colors.backgroundWell,
            border: `1px solid ${enabled ? colors.accentLight : colors.border}`,
            cursor: disabled ? 'not-allowed' : 'pointer',
            position: 'relative',
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.5 : 1,
        }}
    >
        <div
            style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: enabled ? colors.textPrimary : colors.textMuted,
                position: 'absolute',
                top: '2px',
                left: enabled ? '22px' : '2px',
                transition: 'all 0.2s ease',
            }}
        />
    </button>
);

export interface SettingRowProps {
    icon?: React.ReactNode;
    label: string;
    description?: string;
    action?: React.ReactNode;
    onClick?: () => void;
    danger?: boolean;
}

export const SettingRow: React.FC<SettingRowProps> = ({
    icon,
    label,
    description,
    action,
    onClick,
    danger = false,
}) => (
    <div
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            borderBottom: `1px solid ${colors.border}`,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => {
            if (onClick) {
                e.currentTarget.style.background = colors.backgroundCard;
            }
        }}
        onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
            {icon && <div style={{ color: danger ? colors.error : colors.textMuted }}>{icon}</div>}
            <div>
                <div
                    style={{
                        color: danger ? colors.error : colors.textPrimary,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                    }}
                >
                    {label}
                </div>
                {description && (
                    <div
                        style={{
                            color: colors.textMuted,
                            fontSize: '0.75rem',
                            marginTop: '0.25rem',
                        }}
                    >
                        {description}
                    </div>
                )}
            </div>
        </div>
        {action && <div>{action}</div>}
        {onClick && !action && <ChevronRight size={16} style={{ color: colors.textMuted }} />}
    </div>
);

export interface SectionHeaderProps {
    title: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
    <div
        style={{
            padding: '1rem 1rem 0.5rem',
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: colors.textMuted,
        }}
    >
        {title}
    </div>
);

export interface SubscriptionCardProps {
    subscriptionStatus: 'active' | 'trial' | 'expired';
    subscriptionEndDate: string;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
    subscriptionStatus,
    subscriptionEndDate,
}) => {
    const statusLabel =
        subscriptionStatus === 'active'
            ? 'Active'
            : subscriptionStatus === 'trial'
              ? 'Trial'
              : 'Expired';

    const statusColor = subscriptionStatus === 'active' ? colors.success : colors.warning;
    const statusBg =
        subscriptionStatus === 'active' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)';

    const dateText =
        subscriptionStatus === 'active'
            ? `Next billing date: ${new Date(subscriptionEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
            : subscriptionStatus === 'trial'
              ? `Trial ends: ${new Date(subscriptionEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
              : 'Your subscription has expired';

    return (
        <div
            style={{
                margin: '0.5rem 1rem 1rem',
                padding: '1rem',
                background: colors.backgroundCard,
                borderRadius: '0.75rem',
                border: `1px solid ${colors.border}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '1rem' }}>
                        RealSignal Pro
                    </div>
                    <div
                        style={{
                            color: colors.textMuted,
                            fontSize: '0.875rem',
                            marginTop: '0.25rem',
                        }}
                    >
                        $149.99/month
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.75rem',
                        background: statusBg,
                        borderRadius: '1rem',
                        color: statusColor,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    }}
                >
                    <Check size={12} />
                    {statusLabel}
                </div>
            </div>
            <div
                style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: `1px solid ${colors.border}`,
                    color: colors.textMuted,
                    fontSize: '0.75rem',
                }}
            >
                {dateText}
            </div>
        </div>
    );
};

export const CancelSubscriptionButton: React.FC = () => (
    <div style={{ padding: '1rem' }}>
        <button
            style={{
                width: '100%',
                padding: '0.75rem',
                background: 'transparent',
                border: `1px solid ${colors.error}`,
                borderRadius: '0.5rem',
                color: colors.error,
                fontSize: '0.875rem',
                cursor: 'pointer',
            }}
        >
            Cancel Subscription
        </button>
    </div>
);
